import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export type StepProgressStep = {
  title: string;
  description?: string;
  /** Greyed and not clickable (e.g. ticket steps for community events). */
  disabled?: boolean;
  /** Hover text explaining a disabled step. */
  tooltip?: string;
};

export type StepProgressProps = {
  /** 1-based active step number. */
  activeStep: number;
  steps: StepProgressStep[];
  ariaLabel: string;
  /** Presence toggles a clickable <button> vs. a static <div> per step. */
  onStepClick?: (step: number) => void;
  /**
   * Display overrides; default to steps[index].title / .description. Members
   * uses these for the active-step-dependent retitling.
   */
  getStepTitle?: (index: number, activeStep: number) => string;
  getStepDescription?: (
    index: number,
    activeStep: number
  ) => string | undefined;
  /**
   * Full Tailwind literal for the lg+ column template, e.g.
   * "lg:grid-cols-[1.2fr_1fr_1fr_1.2fr]". Must be a static string literal at
   * the call site so the Tailwind JIT scanner sees it.
   */
  desktopGridColsClassName: string;
  /**
   * Renders data-testid={`${stepTestIdPrefix}-${stepNumber}`} on clickable
   * steps when provided (Members uses "member-step").
   */
  stepTestIdPrefix?: string;
  /**
   * Optional final non-interactive item rendered after the numbered steps
   * with an optional muted number circle. It is never clickable, even when
   * onStepClick is provided. Consumers that omit it retain their layout.
   */
  trailingItem?: { title: string; description?: string; number?: number; tooltip?: string };
  /** Applies each approved layout without changing the default wizard. */
  variant?: "default" | "email" | "event";
};

export const StepProgress = ({
  activeStep,
  steps,
  ariaLabel,
  onStepClick,
  getStepTitle,
  getStepDescription,
  desktopGridColsClassName,
  stepTestIdPrefix,
  trailingItem,
  variant = "default",
}: StepProgressProps) => (
  <nav
    aria-label={ariaLabel}
    className={variant === "email"
      ? "sticky top-0 z-20 w-full overflow-x-auto border-b border-[#ECECEC] bg-white lg:-mx-8 lg:w-auto"
      : variant === "event" ? "mt-[16px] w-full border-b border-[#ECECEC] bg-white"
      : "sticky top-0 z-20 w-full border-b border-gray-200 bg-white lg:-mx-8 lg:w-auto"}
  >
    <ol
      className={variant === "email"
        ? "grid w-full grid-cols-1 divide-y divide-gray-100 px-4 lg:flex lg:min-w-[900px] lg:items-end lg:justify-between lg:divide-y-0 lg:px-6"
        : variant === "event" ? `grid w-full grid-cols-1 divide-y divide-gray-100 ${desktopGridColsClassName} lg:divide-y-0`
        : `grid w-full grid-cols-1 divide-y divide-gray-100 px-4 ${desktopGridColsClassName} lg:divide-y-0 lg:px-6`}
    >
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === activeStep;
        // With a trailingItem the last numbered step is no longer the last
        // grid cell, so it keeps its trailing padding.
        const isLastStep = stepNumber === steps.length && !trailingItem;
        // The chevron immediately after the ACTIVE step is gold in the mocks;
        // all other chevrons stay dark.
        const connector = index > 0 && (
          <span className={variant === "email"
            ? "hidden shrink-0 items-center justify-center lg:flex"
            : "hidden w-5 shrink-0 items-center justify-center lg:flex"}>
            <ChevronRightIcon
              aria-hidden="true"
              className={
                index === activeStep
                  ? variant === "email" ? "mx-[18px] text-cpaGold" : "text-cpaGold"
                  : variant === "email" ? "mx-[18px] text-[#9E9E9E]" : variant === "event" ? "text-[#9E9E9E]" : "text-gray-950"
              }
              sx={{ fontSize: variant === "default" ? 22 : 16 }}
            />
          </span>
        );
        const stepContent = (
          <>
            <span
              className={`flex shrink-0 items-center justify-center rounded-full font-bold leading-none ${
                variant === "email" ? "h-[30px] w-[30px] text-[14px]" : variant === "event" ? "h-[34px] w-[34px] text-[14px]" : "h-9 w-9 text-sm"
              } ${
                isActive
                  ? "bg-cpaGold text-white"
                  : "bg-gray-100 text-gray-950"
              }`}
            >
              {stepNumber}
            </span>
            <span className="min-w-0 flex-1">
              <span className={variant === "email"
                ? "block whitespace-nowrap text-[15px] font-bold leading-5 text-[#0A0A0A]"
                : variant === "event" ? "block whitespace-normal text-[14px] font-semibold leading-[18px] text-[#0A0A0A]"
                : "block whitespace-normal break-words text-sm font-bold leading-5 text-gray-950"}>
                {getStepTitle ? getStepTitle(index, activeStep) : step.title}
              </span>
              {variant === "email" ? null : (
                <span className={variant === "event" ? "mt-px block whitespace-normal text-[12px] leading-[15px] text-[#727272]" : "mt-0.5 block whitespace-normal break-words text-xs leading-5 text-gray-900"}>
                  {getStepDescription
                    ? getStepDescription(index, activeStep)
                    : step.description}
                </span>
              )}
            </span>
          </>
        );

        if (variant !== "default") {
          const stepDisabled = Boolean(step.disabled);
          const controlClassName = `${stepDisabled ? "cursor-not-allowed opacity-60 " : ""}${variant === "email"
            ? "flex min-w-0 flex-1 items-center gap-3 border-b-[3px] px-1 pb-[14px] text-left lg:flex-none"
            : "flex min-w-0 flex-1 items-center gap-[12px] border-b-[3px] px-[10px] pt-[12px] pb-[14px] text-left"} ${
            isActive ? "border-cpaGold" : "border-transparent"
          }`;
          return (
            <li key={step.title} aria-current={isActive ? "step" : undefined}
              className={variant === "email" ? "flex min-w-0 items-center py-4 lg:p-0" : "flex min-w-0 items-stretch"}>
              {onStepClick && !stepDisabled ? <button type="button" onClick={() => onStepClick(stepNumber)}
                aria-label={`Go to ${step.title}`}
                data-testid={stepTestIdPrefix ? `${stepTestIdPrefix}-${stepNumber}` : undefined}
                className={`${controlClassName} cursor-pointer bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-cpaGold`}>
                {stepContent}
              </button> : <div className={controlClassName} title={step.tooltip} aria-disabled={stepDisabled || undefined}>{stepContent}</div>}
              {(variant === "email" ? index < steps.length - 1 : !isLastStep) ? <span className={variant === "email" ? "hidden items-center pb-[14px] lg:flex" : "hidden w-[34px] shrink-0 items-center justify-center lg:flex"}>
                <ChevronRightIcon aria-hidden="true" className={`${variant === "email" ? "mx-[18px] " : ""}${isActive ? "text-cpaGold" : "text-[#9E9E9E]"}`} sx={{ fontSize: 16 }} />
              </span> : null}
            </li>
          );
        }

        return (
          <li
            key={step.title}
            aria-current={isActive ? "step" : undefined}
            className={`relative flex min-w-0 items-center gap-2 py-4 ${
              index === 0 ? "pl-0" : "pl-0 lg:pl-3"
            } ${isLastStep ? "pr-0" : "pr-3 lg:pr-3"} ${
              onStepClick ? "cursor-pointer" : ""
            }`}
          >
            {onStepClick ? (
              <button
                type="button"
                onClick={() => onStepClick(stepNumber)}
                // Intentionally the RAW step title, not the resolved display
                // title from getStepTitle (matches the Members donor).
                aria-label={`Go to ${step.title}`}
                data-testid={
                  stepTestIdPrefix
                    ? `${stepTestIdPrefix}-${stepNumber}`
                    : undefined
                }
                className="flex w-full min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md bg-transparent text-left focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold focus-visible:ring-offset-2"
              >
                {connector}
                {stepContent}
              </button>
            ) : (
              <>
                {connector}
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {stepContent}
                </div>
              </>
            )}
            {isActive && (
              <span className="absolute bottom-[-1px] left-0 h-1 w-full bg-cpaGold" />
            )}
          </li>
        );
      })}
      {trailingItem ? (
        // Non-interactive by construction: no button wrapper, no aria-current
        // (jsx-a11y forbids aria-disabled on an implicit listitem role).
        <li className={variant === "event" ? "flex min-w-0 items-center border-b-[3px] border-transparent px-[10px] pt-[12px] pb-[14px]" : "relative flex min-w-0 items-center gap-2 py-4 pl-0 pr-0 lg:pl-3"}>
          {/* The chevron before the trailing item follows the same rule as
              the numbered steps: gold only immediately after the active step
              (activeStep === steps.length), dark otherwise. */}
          {variant !== "event" && <span className="hidden w-5 shrink-0 items-center justify-center lg:flex">
            <ChevronRightIcon
              aria-hidden="true"
              className={
                activeStep === steps.length ? "text-cpaGold" : "text-gray-950"
              }
              sx={{ fontSize: 22 }}
            />
          </span>}
          <div title={trailingItem.tooltip} className={`flex min-w-0 flex-1 items-center ${variant === "event" ? "gap-[12px]" : "gap-2"} ${trailingItem.number !== undefined ? "opacity-40" : ""}`}>
            {trailingItem.number !== undefined ? <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-950">{trailingItem.number}</span> : null}
            <span className="min-w-0 flex-1">
              <span className={variant === "event" ? "block whitespace-normal text-[14px] font-semibold leading-[18px] text-[#0A0A0A]" : "block whitespace-normal break-words text-sm font-bold leading-5 text-gray-950"}>
                {trailingItem.title}
              </span>
              {trailingItem.description ? (
                <span className={variant === "event" ? "mt-px block whitespace-normal text-[12px] leading-[15px] text-[#727272]" : "mt-0.5 block whitespace-normal break-words text-xs leading-5 text-gray-900"}>
                  {trailingItem.description}
                </span>
              ) : null}
            </span>
          </div>
        </li>
      ) : null}
    </ol>
  </nav>
);
